/**
 * RunsTab — per-site run-log list, alert banner, and site-scoped DLQ section.
 *
 * Props {siteId}: fetches publish runs, open alerts, and site-scoped DLQ entries.
 * Accepts optional _initialData for deterministic Storybook rendering.
 */
import { useState, useEffect, useCallback } from "react";
import {
  ActivitySquare,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Inbox,
} from "lucide-react";
import { publishRuns, publishAlerts, dlq } from "../lib/api.js";
import type { PublishRun, PublishAlert, DLQEntry } from "../lib/api.js";

// ─── Helpers ──────────────────────────────────────────────

function relativeDate(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const h = diff / 3_600_000;
  if (h < 24) {
    const m = Math.round(diff / 60_000);
    if (m < 2) return "just now";
    if (m < 60) return `${m}m ago`;
    return `${Math.floor(h)}h ago`;
  }
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// ─── Style tokens ─────────────────────────────────────────

const CARD_CLS =
  "bg-[var(--bg-secondary,theme(colors.gray.900))]/60 rounded-xl border border-gray-700/40 shadow-sm p-5";

const ACTIVE_PILL =
  "bg-indigo-500/20 ring-1 ring-indigo-500/30 text-indigo-300 rounded-full px-2 py-0.5 text-xs";

const STATUS_PILL: Record<PublishRun["status"], string> = {
  scheduled:
    "bg-gray-500/20 ring-1 ring-gray-500/30 text-gray-400 rounded-full px-2 py-0.5 text-xs",
  claimed: ACTIVE_PILL,
  enqueued: ACTIVE_PILL,
  sourcing: ACTIVE_PILL,
  generating: ACTIVE_PILL,
  optimizing: ACTIVE_PILL,
  interlinking: ACTIVE_PILL,
  deploying: ACTIVE_PILL,
  complete:
    "bg-emerald-500/20 ring-1 ring-emerald-500/30 text-emerald-300 rounded-full px-2 py-0.5 text-xs",
  failed:
    "bg-red-500/20 ring-1 ring-red-500/30 text-red-300 rounded-full px-2 py-0.5 text-xs",
};

const STATUS_LABELS: Record<PublishRun["status"], string> = {
  scheduled: "Scheduled",
  claimed: "Claimed",
  enqueued: "Enqueued",
  sourcing: "Sourcing",
  generating: "Generating",
  optimizing: "Optimizing",
  interlinking: "Interlinking",
  deploying: "Deploying",
  complete: "Complete",
  failed: "Failed",
};

const HALT_LABELS: Record<NonNullable<PublishRun["haltReason"]>, string> = {
  BUDGET_EXCEEDED: "Budget exceeded",
  AUTH_ERROR: "Auth error",
  QUOTA_EXCEEDED: "Quota exceeded",
};

// Amber for quota (transient), red for auth (config fix needed), red for budget
const HALT_PILL: Record<NonNullable<PublishRun["haltReason"]>, string> = {
  BUDGET_EXCEEDED:
    "bg-red-500/20 ring-1 ring-red-500/30 text-red-300 rounded-full px-2 py-0.5 text-xs",
  AUTH_ERROR:
    "bg-red-500/20 ring-1 ring-red-500/30 text-red-300 rounded-full px-2 py-0.5 text-xs",
  QUOTA_EXCEEDED:
    "bg-amber-500/20 ring-1 ring-amber-500/30 text-amber-300 rounded-full px-2 py-0.5 text-xs",
};

const REDUCED_MOTION =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ─── Toast ────────────────────────────────────────────────

interface Toast {
  id: number;
  msg: string;
  variant: "success" | "error";
}

let _toastId = 0;

// ─── RunRow ───────────────────────────────────────────────

function RunRow({ run }: { run: PublishRun }) {
  const date = run.runDate ?? run.createdAt;
  const remainingBudget =
    run.budgetCapUsd !== undefined
      ? run.budgetCapUsd - (run.budgetUsedUsd ?? 0)
      : undefined;

  return (
    <div className="flex items-center justify-between gap-4 px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className={STATUS_PILL[run.status]}>{STATUS_LABELS[run.status]}</span>
        {run.haltReason && (
          <span className={HALT_PILL[run.haltReason]}>
            {HALT_LABELS[run.haltReason]}
          </span>
        )}
        {run.clusterId && (
          <span className="rounded-full bg-indigo-500/10 ring-1 ring-indigo-500/20 text-indigo-300 px-2 py-0.5 text-xs truncate max-w-[120px]">
            {run.clusterId}
          </span>
        )}
        <span
          className="font-mono text-xs text-gray-400 whitespace-nowrap"
          title={date}
          aria-label={`Run at ${new Date(date).toISOString()}`}
        >
          {relativeDate(date)}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {remainingBudget !== undefined && (
          <span className="font-mono text-xs text-gray-500" title="Remaining daily budget">
            ${remainingBudget.toFixed(2)} left
          </span>
        )}
        {run.counts.published > 0 && (
          <span className="bg-emerald-500/15 ring-1 ring-emerald-500/25 text-emerald-300 rounded-full px-2 py-0.5 text-xs">
            {run.counts.published} published
          </span>
        )}
        {run.counts.skipped > 0 && (
          <span className="bg-gray-500/15 ring-1 ring-gray-500/25 text-gray-400 rounded-full px-2 py-0.5 text-xs">
            {run.counts.skipped} skipped
          </span>
        )}
        {run.counts.failed > 0 && (
          <span className="bg-red-500/15 ring-1 ring-red-500/25 text-red-300 rounded-full px-2 py-0.5 text-xs">
            {run.counts.failed} failed
          </span>
        )}
      </div>
    </div>
  );
}

// ─── SkeletonRows ─────────────────────────────────────────

function SkeletonRows() {
  return (
    <div className="space-y-2">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={`h-10 rounded-lg bg-gray-700/40 ${REDUCED_MOTION ? "" : "animate-pulse"}`}
        />
      ))}
    </div>
  );
}

// ─── FetchError ───────────────────────────────────────────

function FetchError() {
  return (
    <div className="flex items-center gap-2 py-4 text-xs text-gray-500">
      <AlertTriangle className="w-4 h-4 text-gray-600 flex-shrink-0" />
      Couldn't load data. Refresh to try again.
    </div>
  );
}

// ─── DLQRow ───────────────────────────────────────────────

interface DLQRowProps {
  entry: DLQEntry;
  onRetry: (id: string) => Promise<void>;
}

function DLQRow({ entry, onRetry }: DLQRowProps) {
  const [retrying, setRetrying] = useState(false);
  const isRequeued = !!entry.requeuedAt;
  const isAcknowledged = !!entry.acknowledgedAt;
  const lastError = entry.errorHistory[entry.errorHistory.length - 1];

  async function handleRetry() {
    setRetrying(true);
    try {
      await onRetry(entry._id);
    } finally {
      setRetrying(false);
    }
  }

  return (
    <div
      className={`bg-gray-900 border rounded-xl p-4 ${
        isAcknowledged ? "border-gray-800 opacity-60" : "border-gray-700"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="font-mono text-xs text-gray-400 truncate">
            {entry.queueName} · {entry.taskId}
          </p>
          {lastError && (
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-300 break-words">{lastError.error}</p>
            </div>
          )}
          <p className="text-xs text-gray-500">
            Failed: {relativeDate(entry.deadAt)} · Attempts:{" "}
            {entry.errorHistory.length}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {isRequeued && (
            <span className="text-xs text-emerald-400">requeued</span>
          )}
          {isAcknowledged && !isRequeued && (
            <span className="text-xs text-gray-500">acknowledged</span>
          )}
          <button
            onClick={() => void handleRetry()}
            disabled={retrying || isRequeued || isAcknowledged}
            aria-label={`Retry failed item ${entry.taskId}`}
            className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-xs rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-900"
          >
            {retrying ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── InitialData type for deterministic Storybook ─────────

export interface RunsTabInitialData {
  runs?: PublishRun[];
  alerts?: PublishAlert[];
  dlqEntries?: DLQEntry[];
}

// ─── Main RunsTab component ───────────────────────────────

export interface RunsTabProps {
  siteId: string;
  /** Pass deterministic data to bypass live fetch (Storybook only). */
  _initialData?: RunsTabInitialData;
}

export function RunsTab({ siteId, _initialData }: RunsTabProps) {
  const isDeterministic = _initialData !== undefined;

  const [runs, setRuns] = useState<PublishRun[]>(_initialData?.runs ?? []);
  const [alerts, setAlerts] = useState<PublishAlert[]>(_initialData?.alerts ?? []);
  const [dlqEntries, setDlqEntries] = useState<DLQEntry[]>(_initialData?.dlqEntries ?? []);

  const [loadingRuns, setLoadingRuns] = useState(!isDeterministic);
  const [loadingAlerts, setLoadingAlerts] = useState(!isDeterministic);
  const [loadingDlq, setLoadingDlq] = useState(!isDeterministic);

  const [errRuns, setErrRuns] = useState<string | null>(null);
  const [errAlerts, setErrAlerts] = useState<string | null>(null);
  const [errDlq, setErrDlq] = useState<string | null>(null);

  const [ackLoading, setAckLoading] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);

  function addToast(msg: string, variant: Toast["variant"]) {
    const id = ++_toastId;
    setToasts((prev) => [...prev, { id, msg, variant }]);
    if (variant === "success") {
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
    }
  }

  const fetchRuns = useCallback(async () => {
    setLoadingRuns(true);
    setErrRuns(null);
    try {
      const res = await publishRuns.list(siteId, 50);
      setRuns(res.data);
    } catch {
      setErrRuns("fetch-failed");
    } finally {
      setLoadingRuns(false);
    }
  }, [siteId]);

  const fetchAlerts = useCallback(async () => {
    setLoadingAlerts(true);
    setErrAlerts(null);
    try {
      const res = await publishAlerts.list(siteId);
      setAlerts(res.data);
    } catch {
      setErrAlerts("fetch-failed");
    } finally {
      setLoadingAlerts(false);
    }
  }, [siteId]);

  const fetchDlq = useCallback(async () => {
    setLoadingDlq(true);
    setErrDlq(null);
    try {
      const res = await dlq.list({ siteId });
      setDlqEntries(res.data);
    } catch {
      setErrDlq("fetch-failed");
    } finally {
      setLoadingDlq(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (isDeterministic) return;
    void fetchRuns();
    void fetchAlerts();
    void fetchDlq();
  }, [siteId, isDeterministic, fetchRuns, fetchAlerts, fetchDlq]);

  // ─── Acknowledge all open alerts ──────────────────────────
  async function handleAcknowledgeAll() {
    const open = alerts.filter((a) => a.status === "open");
    if (!open.length) return;
    setAckLoading(true);
    const prevAlerts = alerts;
    setAlerts((prev) => prev.filter((a) => a.status !== "open")); // optimistic
    try {
      await Promise.all(open.map((a) => publishAlerts.acknowledge(a._id)));
      await fetchAlerts();
    } catch {
      setAlerts(prevAlerts); // revert
      addToast("Couldn't acknowledge — try again.", "error");
    } finally {
      setAckLoading(false);
    }
  }

  // ─── DLQ retry ────────────────────────────────────────────
  async function handleRetry(id: string) {
    try {
      await dlq.requeue(id);
      await fetchDlq();
      addToast("Requeued.", "success");
    } catch (e: unknown) {
      const reason = e instanceof Error ? e.message : "unknown error";
      addToast(`Couldn't retry — ${reason}. Try again.`, "error");
    }
  }

  const openAlerts = alerts.filter((a) => a.status === "open");
  const oldestOpen = openAlerts.reduce<string | null>((acc, a) => {
    if (!acc) return a.createdAt;
    return a.createdAt < acc ? a.createdAt : acc;
  }, null);

  // ─── Render ────────────────────────────────────────────────

  return (
    <div className="space-y-5 max-w-3xl">

      {/* Toast stack */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium shadow-lg ${
                t.variant === "success"
                  ? "bg-emerald-900 text-emerald-300 border border-emerald-700"
                  : "bg-red-900 text-red-300 border border-red-700"
              }`}
            >
              {t.variant === "error" && <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />}
              {t.msg}
              {t.variant === "error" && (
                <button
                  onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
                  className="ml-1 text-red-400 hover:text-red-200"
                  aria-label="Dismiss"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ═══ ALERT BANNER ═══ */}
      {!loadingAlerts && !errAlerts && openAlerts.length > 0 && (
        <div
          role="alert"
          className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start justify-between gap-4"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-gray-100">Run alert</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {openAlerts.length === 1
                  ? "1 missed or failed run. Acknowledge to dismiss."
                  : `${openAlerts.length} missed or failed runs since ${oldestOpen ? relativeDate(oldestOpen) : "recently"}.`}
                {openAlerts.some((a) => a.clusterId) && (
                  <span className="ml-1">
                    Cluster:{" "}
                    {[...new Set(openAlerts.map((a) => a.clusterId).filter(Boolean))].join(", ")}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={() => void handleAcknowledgeAll()}
            disabled={ackLoading}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-900"
          >
            {ackLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Acknowledge all
          </button>
        </div>
      )}

      {/* ═══ RUN HISTORY ═══ */}
      <div className={CARD_CLS}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold text-gray-100">Run History</p>
          <button
            onClick={() => void fetchRuns()}
            disabled={loadingRuns || isDeterministic}
            title="Refresh run history"
            aria-label="Refresh run history"
            className="p-3 text-gray-500 hover:text-gray-300 disabled:opacity-40 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-lg"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {loadingRuns && <SkeletonRows />}
        {!loadingRuns && errRuns && <FetchError />}
        {!loadingRuns && !errRuns && runs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
            <ActivitySquare className="w-6 h-6 text-gray-600" />
            <p className="text-sm text-gray-500">
              No runs yet. Publishing will appear here after the first scheduled run.
            </p>
          </div>
        )}
        {!loadingRuns && !errRuns && runs.length > 0 && (
          <div className="divide-y divide-gray-700/30">
            {runs.map((run) => (
              <RunRow key={run._id} run={run} />
            ))}
          </div>
        )}
      </div>

      {/* ═══ SITE-SCOPED DLQ ═══ */}
      <div className={CARD_CLS}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold text-gray-100">Failed Publish Steps</p>
          <button
            onClick={() => void fetchDlq()}
            disabled={loadingDlq || isDeterministic}
            title="Refresh failed steps"
            aria-label="Refresh failed publish steps"
            className="p-3 text-gray-500 hover:text-gray-300 disabled:opacity-40 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-lg"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {loadingDlq && <SkeletonRows />}
        {!loadingDlq && errDlq && <FetchError />}
        {!loadingDlq && !errDlq && dlqEntries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
            <Inbox className="w-6 h-6 text-gray-600" />
            <p className="text-sm text-gray-500">No failed publish steps for this site.</p>
          </div>
        )}
        {!loadingDlq && !errDlq && dlqEntries.length > 0 && (
          <div className="space-y-3">
            {dlqEntries.map((entry) => (
              <DLQRow key={entry._id} entry={entry} onRetry={handleRetry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
